import React, { useState, useEffect, useMemo } from 'react';
import { FaUser, FaBuilding, FaCreditCard, FaClock, FaUsers, FaUserGraduate, FaSync, FaChartLine } from 'react-icons/fa';
import CustomModal from '../../Global/Modal/CustomModal';
import { getUserGroups, getUserStudents, clearUserStatsCache } from '../SuperFirebaseFunctions';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import './UserModal.css';

const UserModal = ({ show, handleClose, user }) => {
    const [loadStats, setLoadStats] = useState({
        students: [],
        groups: [],
        isLoading: true,
        error: null,
        lastUpdated: null
    });

    // Process student data for the graph
    const studentSignupData = useMemo(() => {
        if (!loadStats.students.length) return [];

        const today = new Date();
        const lastWeek = new Date(today);
        lastWeek.setDate(today.getDate() - 7);

        // Initialize data array with last 7 days
        const data = Array.from({ length: 7 }, (_, i) => {
            const date = new Date(today);
            date.setDate(today.getDate() - i);
            return {
                date: date.toLocaleDateString('he-IL', { weekday: 'short', day: 'numeric' }),
                signups: 0,
                timestamp: date.getTime()
            };
        }).reverse();

        // Count signups for each day
        loadStats.students.forEach(student => {
            if (student.studentCreatedAt) {
                let signupDate;
                if (student.studentCreatedAt.toDate) {
                    signupDate = student.studentCreatedAt.toDate();
                } else if (student.studentCreatedAt.seconds) {
                    signupDate = new Date(student.studentCreatedAt.seconds * 1000);
                } else if (typeof student.studentCreatedAt === 'number') {
                    signupDate = new Date(student.studentCreatedAt);
                } else if (typeof student.studentCreatedAt === 'string') {
                    signupDate = new Date(student.studentCreatedAt);
                }

                if (signupDate && signupDate >= lastWeek && signupDate <= today) {
                    const dayIndex = Math.floor((today - signupDate) / (1000 * 60 * 60 * 24));
                    if (dayIndex >= 0 && dayIndex < 7) {
                        data[6 - dayIndex].signups++;
                    }
                }
            }
        });

        return data;
    }, [loadStats.students]);

    const fetchUserStats = async (forceRefresh = false) => {
        if (show && user) {
            try {
                setLoadStats(prev => ({ ...prev, isLoading: true, error: null }));
                const [students, groups] = await Promise.all([
                    getUserStudents(user.userId, forceRefresh),
                    getUserGroups(user.userId, forceRefresh)
                ]);
                setLoadStats({
                    students,
                    groups,
                    isLoading: false,
                    error: null,
                    lastUpdated: new Date()
                });
            } catch (error) {
                console.error('Error fetching user stats:', error);
                setLoadStats(prev => ({
                    ...prev,
                    isLoading: false,
                    error: 'Failed to load statistics'
                }));
            }
        }
    };

    useEffect(() => {
        fetchUserStats();
        // Clear cache when modal is closed
        return () => {
            if (user) {
                clearUserStatsCache(user.userId);
            }
        };
    }, [show, user]);

    const handleRefresh = () => {
        fetchUserStats(true);
    };

    if (!user) return null;

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        let date;
        if (timestamp.toDate) {
            date = timestamp.toDate();
        } else if (
            typeof timestamp === 'object' &&
            typeof timestamp.seconds === 'number' &&
            typeof timestamp.nanoseconds === 'number'
        ) {
            date = new Date(timestamp.seconds * 1000 + Math.floor(timestamp.nanoseconds / 1e6));
        } else if (timestamp instanceof Date) {
            date = timestamp;
        } else if (typeof timestamp === 'number') {
            date = new Date(timestamp);
        } else if (typeof timestamp === 'string') {
            const parsed = Date.parse(timestamp);
            if (!isNaN(parsed)) {
                date = new Date(parsed);
            } else {
                return 'N/A';
            }
        } else {
            return 'N/A';
        }
        return date.toLocaleString('he-IL', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            active: { text: 'Active', class: 'status-badge active' },
            disabled: { text: 'Disabled', class: 'status-badge disabled' },
            waitingForOrgAccept: { text: 'Waiting for Organization', class: 'status-badge waiting' }
        };
        const statusInfo = statusMap[status] || { text: status, class: 'status-badge' };
        return <span className={statusInfo.class}>{statusInfo.text}</span>;
    };

    const getTypeBadge = (type) => {
        const typeMap = {
            manager: { text: 'Manager', class: 'type-badge manager' },
            coach: { text: 'Coach', class: 'type-badge coach' }
        };
        const typeInfo = typeMap[type] || { text: type, class: 'type-badge' };
        return <span className={typeInfo.class}>{typeInfo.text}</span>;
    };

    const footerButtons = [
        {
            title: 'Close',
            style: 'grey',
            onClick: handleClose
        }
    ];

    return (
        <CustomModal
            isOpen={show}
            onClose={handleClose}
            title={user.userName}
            subtitle="User Details"
            width={50}
            height={90}
            footerButtons={footerButtons}
        >
            <div className="user-modal-content">
                <div className="sc-user-modal-table">
                    {/* Basic Information */}
                    <div className="sc-user-modal-section">
                        <div className="sc-user-modal-header">
                            <FaUser className="section-icon" />
                            <h3>Basic Information</h3>
                        </div>
                        <div className="sc-user-modal-grid">
                            <div className="sc-user-modal-row">
                                <div className="sc-user-modal-cell">
                                    <label>userId</label>
                                    <span>{user.userId}</span>
                                </div>
                                <div className="sc-user-modal-cell">
                                    <label>userName</label>
                                    <span>{user.userName}</span>
                                </div>
                            </div>
                            <div className="sc-user-modal-row">
                                <div className="sc-user-modal-cell">
                                    <label>userEmailAddress</label>
                                    <span>{user.userEmailAddress}</span>
                                </div>
                                <div className="sc-user-modal-cell">
                                    <label>userPhoneNumber</label>
                                    <span>{user.userPhoneNumber || 'Not specified'}</span>
                                </div>
                            </div>
                            <div className="sc-user-modal-row">
                                <div className="sc-user-modal-cell">
                                    <label>userPassword</label>
                                    <span>{user.userPassword || 'Not set'}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Account Status */}
                    <div className="sc-user-modal-section">
                        <div className="sc-user-modal-header">
                            <FaBuilding className="section-icon" />
                            <h3>Account Status</h3>
                        </div>
                        <div className="sc-user-modal-grid">
                            <div className="sc-user-modal-row">
                                <div className="sc-user-modal-cell">
                                    <label>userStatus</label>
                                    <span>{getStatusBadge(user.userStatus)}</span>
                                </div>
                                <div className="sc-user-modal-cell">
                                    <label>userType</label>
                                    <span>{getTypeBadge(user.userType)}</span>
                                </div>
                            </div>
                            <div className="sc-user-modal-row">
                                <div className="sc-user-modal-cell">
                                    <label>userField</label>
                                    <span>{user.userField || 'Not specified'}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Organization */}
                    <div className="sc-user-modal-section">
                        <div className="sc-user-modal-header">
                            <FaBuilding className="section-icon" />
                            <h3>Organization Details</h3>
                        </div>
                        <div className="sc-user-modal-grid">
                            <div className="sc-user-modal-row">
                                <div className="sc-user-modal-cell">
                                    <label>userOrganizationName</label>
                                    <span>{user.userOrganizationName || 'Not specified'}</span>
                                </div>
                                <div className="sc-user-modal-cell">
                                    <label>organizationCode</label>
                                    <span>{user.userOrganization?.organizationCode || 'Not specified'}</span>
                                </div>
                            </div>
                            <div className="sc-user-modal-row">
                                <div className="sc-user-modal-cell">
                                    <label>organizationOwnerID</label>
                                    <span>{user.userOrganization?.organizationOwnerID || 'Not specified'}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Payment */}
                    <div className="sc-user-modal-section">
                        <div className="sc-user-modal-header">
                            <FaCreditCard className="section-icon" />
                            <h3>Payment Information</h3>
                        </div>
                        <div className="sc-user-modal-grid">
                            <div className="sc-user-modal-row">
                                <div className="sc-user-modal-cell">
                                    <label>plan</label>
                                    <span>{user.paymentDetails?.plan || 'Not specified'}</span>
                                </div>
                                <div className="sc-user-modal-cell">
                                    <label>didPay</label>
                                    <span className={user.paymentDetails?.didPay ? 'sc-user-modal-payment-status paid' : 'sc-user-modal-payment-status unpaid'}>
                                        {user.paymentDetails?.didPay ? 'Paid' : 'Unpaid'}
                                    </span>
                                </div>
                            </div>
                            <div className="sc-user-modal-row">
                                <div className="sc-user-modal-cell">
                                    <label>trialPeriodInMonths</label>
                                    <span>{user.paymentDetails?.trialPeriodInMonths || 0} months</span>
                                </div>
                                <div className="sc-user-modal-cell">
                                    <label>lastPaymentDate</label>
                                    <span>{formatDate(user.paymentDetails?.lastPaymentDate)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Timestamps */}
                    <div className="sc-user-modal-section">
                        <div className="sc-user-modal-header">
                            <FaClock className="section-icon" />
                            <h3>Timestamps</h3>
                        </div>
                        <div className="sc-user-modal-grid">
                            <div className="sc-user-modal-row">
                                <div className="sc-user-modal-cell">
                                    <label>userCreatedAt</label>
                                    <span>{formatDate(user.userCreatedAt)}</span>
                                </div>
                                <div className="sc-user-modal-cell">
                                    <label>lastUpdated</label>
                                    <span>{formatDate(user.lastUpdated)}</span>
                                </div>
                            </div>
                            <div className="sc-user-modal-row">
                                <div className="sc-user-modal-cell">
                                    <label>lastPasswordUpdate</label>
                                    <span>{formatDate(user.lastPasswordUpdate)}</span>
                                </div>
                                <div className="sc-user-modal-cell">
                                    <label>lastLoginAt</label>
                                    <span>{formatDate(user.lastLoginAt)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Statistics Section */}
                    <div className="sc-user-modal-section">
                        <div className="sc-user-modal-header">
                            <FaUsers className="section-icon" />
                            <h3>Statistics</h3>
                            <div className="sc-user-modal-actions">
                                <button
                                    className="sc-user-modal-refresh-button"
                                    onClick={handleRefresh}
                                    title="Refresh statistics"
                                >
                                    <FaSync className={loadStats.isLoading ? 'rotating' : ''} />
                                </button>
                                {loadStats.lastUpdated && (
                                    <span className="sc-user-modal-last-updated">
                                        Last updated: {formatDate(loadStats.lastUpdated)}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="sc-user-modal-grid">
                            <div className="sc-user-modal-row">
                                <div className="sc-user-modal-cell">
                                    <label>Students</label>
                                    <div className="sc-user-modal-stats-container">
                                        {loadStats.isLoading ? (
                                            <span className="sc-user-modal-loading-text">Loading...</span>
                                        ) : loadStats.error ? (
                                            <span className="sc-user-modal-error-text">{loadStats.error}</span>
                                        ) : (
                                            <span className="sc-user-modal-stats-value">{loadStats.students.length}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="sc-user-modal-cell">
                                    <label>Groups</label>
                                    <div className="sc-user-modal-stats-container">
                                        {loadStats.isLoading ? (
                                            <span className="sc-user-modal-loading-text">Loading...</span>
                                        ) : loadStats.error ? (
                                            <span className="sc-user-modal-error-text">{loadStats.error}</span>
                                        ) : (
                                            <span className="sc-user-modal-stats-value">{loadStats.groups.length}</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Student Signups Graph Section */}
                        <div className="sc-user-modal-header" style={{ marginTop: '20px' }}>
                            <FaChartLine className="section-icon" />
                            <h3>Student Signups - Last 7 Days</h3>
                        </div>
                        <div className="sc-user-modal-graph-container">
                            {loadStats.isLoading ? (
                                <div className="sc-user-modal-loading-text">Loading graph data...</div>
                            ) : loadStats.error ? (
                                <div className="sc-user-modal-error-text">{loadStats.error}</div>
                            ) : (
                                <ResponsiveContainer width="100%" height={300}>
                                    <LineChart
                                        data={studentSignupData}
                                        margin={{
                                            top: 20,
                                            right: 30,
                                            left: 20,
                                            bottom: 10,
                                        }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="date"
                                            tick={{ fontSize: 12 }}
                                            tickLine={false}
                                        />
                                        <YAxis
                                            allowDecimals={false}
                                            tick={{ fontSize: 12 }}
                                            tickLine={false}
                                        />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: '#fff',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                fontSize: '12px'
                                            }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="signups"
                                            stroke="#1976d2"
                                            strokeWidth={2}
                                            dot={{ fill: '#1976d2', strokeWidth: 2 }}
                                            activeDot={{ r: 6 }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </CustomModal>
    );
};

export default UserModal; 